import { createColumnHelper } from "@tanstack/react-table"
import type { ColumnDef } from "@tanstack/react-table"
import type { EngagementFinanceRow } from "@/types/report"

const columnHelper = createColumnHelper<EngagementFinanceRow>()

export const engagementFinanceColumns: ColumnDef<EngagementFinanceRow, any>[] = [
  // This report is one row per engagement, so grouping by engagement produces groups of one.
  // "Engagement roll-up" below replaces it as the only grouping that says anything here.
  columnHelper.accessor("engagementCode", {
    header: "Code",
    size: 120,
    meta: { type: "string" },
    enableGrouping: false,
  }),
  columnHelper.accessor("engagementName", {
    header: "Engagement",
    size: 240,
    meta: { type: "string" },
    enableGrouping: false,
  }),
  // GW-231: the chain root, used as the grouping key behind the "Roll-up engagements" toggle (wired
  // up via rollUpColumnId in the column registry, not chosen from Group By). A grouping key rather
  // than a data column, so it is hidden from the grid body by default.
  columnHelper.accessor("rollUpEngagement", {
    header: "Roll-up",
    size: 240,
    meta: { type: "string" },
    enableHiding: true,
  }),
  columnHelper.accessor("engagementStatus", {
    header: "Status",
    size: 130,
    meta: { type: "status", uniqueValues: true },
  }),
  columnHelper.accessor("client", {
    header: "Client",
    size: 200,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("billingType", {
    header: "Billing Type",
    size: 140,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("startDate", {
    header: "Start Date",
    size: 210,
    meta: { type: "date", dateRangePair: "endDate" },
  }),
  columnHelper.accessor("endDate", {
    header: "End Date",
    size: 210,
    meta: { type: "date", dateRangePair: "startDate" },
  }),
  // The currency the money columns below are in, and what the cell formatter and the group-total
  // currency guard read off the row. Hidden by default, as on Time Entries: the figures carry their
  // own symbol, so the column is here for filtering and for reading the code itself.
  columnHelper.accessor("currency", {
    header: "Currency",
    size: 110,
    meta: { type: "string", uniqueValues: true },
    enableHiding: true,
  }),
  columnHelper.accessor("contractAmount", {
    header: "Contract Amount",
    size: 170,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("projectedCost", {
    header: "Projected Cost",
    size: 160,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("billedToDate", {
    header: "Billed to Date",
    size: 160,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("revenueRecognitionToDate", {
    header: "Revenue Recognition",
    size: 190,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("actualCostToDate", {
    header: "Actual Cost",
    size: 150,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("billableCost", {
    header: "Billable Cost",
    size: 150,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("nonBillableCost", {
    header: "Non-Billable Cost",
    size: 170,
    meta: { type: "currency" },
  }),
  // Recomputed for a chain total from the summed parts, mirroring spEngagementTable's own
  // (summed Actual Cost / summed Projected Cost) * 100. Summing the members' percentages would be wrong.
  columnHelper.accessor("percentActualToProjected", {
    header: "% Actual/Projected",
    size: 175,
    meta: {
      type: "percent",
      groupTotalRatio: { numerator: "actualCostToDate", denominator: "projectedCost" },
    },
  }),
  columnHelper.accessor("grossProfit", {
    header: "Gross Profit",
    size: 150,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("unbilledRevenue", {
    header: "Unbilled Revenue",
    size: 170,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("remainingContractAmount", {
    header: "Remaining Contract",
    size: 180,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("engagementType", {
    header: "Engagement Type",
    size: 170,
    meta: { type: "string", uniqueValues: true },
    enableHiding: true,
  }),
  columnHelper.accessor("requestType", {
    header: "Request Type",
    size: 150,
    meta: { type: "string", uniqueValues: true },
    enableHiding: true,
  }),
  columnHelper.accessor("engagementLead", {
    header: "Engagement Lead",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("deliveryManager", {
    header: "Delivery Manager",
    size: 180,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("branch", {
    header: "Branch",
    size: 120,
    meta: { type: "string", uniqueValues: true },
  }),
  columnHelper.accessor("projectedHours", {
    header: "Projected Hours",
    size: 150,
    meta: { type: "number" },
  }),
  columnHelper.accessor("hoursToDate", {
    header: "Hours to Date",
    size: 140,
    meta: { type: "number" },
  }),
  // A headcount, not an additive measure: a worker staffed on two members of the same chain would be
  // counted twice, so a chain total would overstate the real number of people involved.
  columnHelper.accessor("numberOfResources", {
    header: "Resources",
    size: 120,
    meta: { type: "integer", excludeFromGroupTotal: true },
  }),
  columnHelper.accessor("currentMonthRevenue", {
    header: "Current Mo. Revenue",
    size: 180,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("currentMonthCost", {
    header: "Current Mo. Cost",
    size: 170,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("ytdRevenue", {
    header: "YTD Revenue",
    size: 150,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("ytdCost", {
    header: "YTD Cost",
    size: 140,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("projectToDateRevenue", {
    header: "Project-to-Date Revenue",
    size: 200,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("projectToDateCost", {
    header: "Project-to-Date Cost",
    size: 190,
    meta: { type: "currency" },
  }),
  columnHelper.accessor("asOfDate", {
    header: "As of Date",
    size: 160,
    meta: { type: "date" },
  }),
]

export const defaultHiddenColumns: Record<string, boolean> = {
  currency: false,
  rollUpEngagement: false,
}
